import { TextField, TextArea, Label } from '@heroui/react';

import {
  useController,
  UseControllerProps,
  FieldValues,
  Path
} from 'react-hook-form';
import { useMemo } from 'react';
import { useGlobalComponent } from '@rhino-project/core/hooks';
import { stripRhinoProps } from './utils/stripRhinoProps';

export type DisplayTextareaProps<T extends FieldValues = FieldValues> = Omit<
  UseControllerProps<T>,
  'name' | 'control'
> & {
  accessor?: (value: unknown) => string | null | undefined;
  empty?: string;
  path: Path<T>;
  label?: string;
  className?: string;
  [key: string]: unknown;
};

export const DisplayTextareaBase = <T extends FieldValues = FieldValues>({
  accessor,
  empty = '-',
  path,
  rules,
  shouldUnregister,
  defaultValue,
  disabled,
  label,
  className,
  ...props
}: DisplayTextareaProps<T>) => {
  const {
    field: { value: fieldValue, ...fieldProps }
  } = useController({
    name: path,
    rules,
    shouldUnregister,
    defaultValue,
    disabled
  });

  const value = useMemo(
    () => (accessor ? accessor(fieldValue) : fieldValue) ?? empty,
    [accessor, fieldValue, empty]
  );

  return (
    <TextField
      {...fieldProps}
      isReadOnly
      value={value}
      className={className}
      {...stripRhinoProps(props)}
    >
      {label && <Label>{label}</Label>}
      <TextArea autoComplete="off" />
    </TextField>
  );
};

export const DisplayTextarea = <T extends FieldValues = FieldValues>(
  props: DisplayTextareaProps<T>
) => useGlobalComponent('DisplayTextarea', DisplayTextareaBase, props);
